// The head module for every prerendered page under app/routes/: the /series show + episode pages' JSON-LD, and the five marketing pages' `<head>` (`splashMeta` + `softwareApplicationLd`). D1 forbids importing OCDevel's podcast-ld/meta-helpers/MLG machinery — this is the whole surface those pages need, not a general-purpose port.
import type { MetaDescriptor } from 'react-router';
import { numberedEpisodeTitle, type ShowDetail, type ShowEpisode } from './series-data';

export const ORIGIN = 'https://gnothiai.com';

/** Strip markdown noise + collapse whitespace + truncate to a clean ~155-char SEO description. */
export function metaDescription(text: string | null | undefined): string {
  const t = (text ?? '').replace(/[#*_`>[\]()]/g, '').replace(/\s+/g, ' ').trim();
  return t.length > 155 ? `${t.slice(0, 152)}...` : t;
}

export function breadcrumbLdJson(trail: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: ORIGIN },
      ...trail.map((c, i) => ({ '@type': 'ListItem', position: i + 2, name: c.name, item: `${ORIGIN}${c.path}` })),
    ],
  };
}

/** Synthetic-media disclosure for the JSON-LD `description`: no schema.org property models AI narration, so it rides the human-readable text (same reason as the RSS builder's `<description>`). Empty in → the sentence alone, never a dangling fragment.
 * NO provenance strip here, deliberately: every `ShowDetail`/`ShowEpisode` field this module renders comes from the listed-shows artifact and nowhere else (series-data.ts fetches that one JSON), and `directory.ts`'s `publicShow`/`withContent` strip every prose field at write. Stripping again here would be a second copy of the regex on the web side for text already proven clean at its only writer. If a non-artifact source is ever wired into these pages, that source strips — not this file. */
const withDisclosure = (text: string | null | undefined, disclosure = "This show's audio is narrated by AI-generated synthetic voices.") => `${(text ?? '').trim().replace(/([^.!?])$/, '$1.')} ${disclosure}`.trim();

/** Search intent for a show is "<topic> podcast" ("claude code podcast"), never "<brand> <topic>" — so the word rides the HEAD TAGS of both prerendered show URLs even though it was deliberately retired from the show title field (`stories.name`) and the RSS channel title. Idempotent: a title already carrying it gets no second copy (four of six in-repo title mirrors still read "… Podcast", so this arm is real).
 * Title/og:title ONLY, by design: the `<h1>` renders `show.title` raw (re-adding it there would visually undo the retirement), the JSON-LD `name` must keep matching the RSS channel title (one show, one structured name), and `metaDescription` truncates at 155 so an appended keyword is silently cut. */
export function podcastTitle(showTitle: string): string {
  return /podcast/i.test(showTitle) ? showTitle : `${showTitle} Podcast`;
}

export function seriesUrl(slug: string) { return `${ORIGIN}/series/${slug}`; }
export function episodeUrl(slug: string, id: string) { return `${ORIGIN}/series/${slug}/${id}`; }

export function buildPodcastSeriesLd(show: ShowDetail) {
  const url = seriesUrl(show.slug);
  const parts = show.episodes.filter((e) => e.id != null);
  return {
    '@context': 'https://schema.org',
    '@type': 'PodcastSeries',
    '@id': `${url}#series`,
    name: show.title,
    url,
    inLanguage: show.language ?? 'en',
    description: withDisclosure(show.description),
    ...(show.cover_url && { image: show.cover_url }),
    ...(show.feed_url && { webFeed: show.feed_url }),
    ...(parts.length && {
      numberOfEpisodes: parts.length,
      hasPart: parts.map((e, i) => ({
        '@type': 'PodcastEpisode', position: i + 1, url: episodeUrl(show.slug, e.id), name: numberedEpisodeTitle(e, show),
        image: show.cover_url,
      })),
    }),
  };
}

/**
 * The ten head descriptors every marketing page ships identically — the ONLY way one of them declares its head. Append the page's own `script:ld+json` after it; never hand-roll a descriptor this returns.
 * They are DESCRIPTORS, never literal `<head>` tags, and they are repeated per page rather than inherited: React Router replaces the parent's descriptor array with the leaf's WHOLESALE, so root.tsx's title/og never reach a prerendered leaf and a leaf that omits og:image ships none at all (measured in build/client/index.html, not inferred). og:image is absolute because scrapers do not reliably resolve a relative one.
 * A page that stops calling this also stops exporting `meta()`, at which point it inherits root.tsx's `robots: noindex,follow` — hence the build-artifact `noindex` grep over the five prerendered pages.
 */
export function splashMeta({ title, description, path }: { title: string; description: string; path: string }): MetaDescriptor[] {
  const url = `${ORIGIN}${path}`;
  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: url },
    { property: 'og:image', content: `${ORIGIN}/og-image.jpg` },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:type', content: 'website' },
    { tagName: 'link', rel: 'canonical', href: url },
  ];
}

/** The marketing pages' product LD. `price: '0'` is truthful — narration is free and paid capabilities are credit top-ups, not a subscription. Shared rather than per-page because a field one page spells differently is invisible to tsc inside a JSON-LD object. The `url` stays a PER-PAGE argument: /series points its LD at `#podcast-generator` while its og:url and canonical stay `/series`, and a helper that flattened that asymmetry would break the LD. */
export function softwareApplicationLd({ name, description, url, applicationCategory, featureList }: {
  name: string; description: string; url: string; applicationCategory: string; featureList?: string[];
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name, description, url, applicationCategory,
    operatingSystem: 'Web',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    ...(featureList && { featureList }),
  };
}

// "HH:MM:SS" seconds → ISO-8601 PT#H#M#S (schema.org duration).
function isoDuration(secs: number | null): string | undefined {
  if (!secs) return undefined;
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = Math.floor(secs % 60);
  return `PT${h ? `${h}H` : ''}${m ? `${m}M` : ''}${s ? `${s}S` : ''}`;
}

export function buildPodcastEpisodeLd(show: ShowDetail, episode: ShowEpisode) {
  const url = episodeUrl(show.slug, episode.id);
  return {
    '@context': 'https://schema.org',
    '@type': 'PodcastEpisode',
    url, name: numberedEpisodeTitle(episode, show),
    description: withDisclosure(episode.teaser, 'This episode is narrated by AI-generated synthetic voices.'),
    ...(episode.created_at && { datePublished: episode.created_at }),
    image: show.cover_url,
    ...(isoDuration(episode.final_duration_seconds) && { duration: isoDuration(episode.final_duration_seconds) }),
  };
}
